// @ts-nocheck
import { WORKMARKET_CONTRACTOR_TYPES } from "../../constants/configs";
import { getCssVariableHandler } from "../../styles/getCssVariables";
import { getEmailSanitized } from "../formatters";

/** A real staff-assigned classification is required before profile verification. */
export const hasAssignedContractorType = (contractorType) => {
  const normalized = String(contractorType ?? "").trim().toLowerCase();
  return !!normalized && !["na", "n/a", "tbd"].includes(normalized);
};

/**
 * Normalizes `preferredAirportIds` / `additionalAirportIds` for the write payload.
 *
 * 🔴 Why this exists (bug found 2026-08-08): the previous code was
 * `.filter(item => item?.Id?._id).map(item => ({ Id: item?.Id?._id, radius }))`,
 * which only works when the ref is POPULATED. The new backend's `getContractorById`
 * had lost the legacy `populate`, so `Id` arrived as a raw ObjectId, the filter
 * **dropped every row**, and the PUT went out with `[]`. Since
 * `editPersonalInformation` runs `$set`, opening a contractor's Edit and saving
 * **wiped their airports** — silently, with the screen showing "Success".
 *
 * The backend is fixed (it resolves the ref again), but this filter was still a
 * landmine: any path serving the raw id would clear the data all over. Both shapes
 * are now accepted — populated object OR raw id — and only genuinely empty entries
 * (the blank `{ Id: {}, radius: 50 }` row the form starts with) are dropped.
 *
 * @param {Array<{Id?: any, radius?: number}>} [list]
 * @returns {Array<{Id: string, radius: number}>}
 */
export const toAirportIdsPayload = (list) => {
  if (!Array.isArray(list)) return [];
  return list
    .map((item) => {
      const ref = item?.Id;
      // populated object -> _id; string/ObjectId -> itself
      const id =
        ref && typeof ref === "object" ? ref?._id ?? ref?.id ?? null : ref ?? null;
      return id ? { Id: String(id), radius: item?.radius } : null;
    })
    .filter(Boolean);
};

/**
 * Resolve a contractor option's display name with a safe fallback chain so the
 * UI never renders "undefined undefined".
 * Order: contractorDetails.name -> contractorDetails.fullName ->
 * "firstName lastName" (blank parts dropped) -> contractorDetails.email -> "—".
 * @param {object} [option]
 * @returns {string}
 */
export const getContractorOptionName = (option) => {
  const d = option?.contractorDetails ?? option ?? {};
  return (
    d?.name ||
    d?.fullName ||
    [d?.firstName, d?.lastName].filter(Boolean).join(" ") ||
    d?.email ||
    "—"
  );
};

/**
 * Onboarding completeness behind the yellow `pendingCompletion` name color in
 * Session Management / Assign Contractor.
 *
 * 🔴 Two bugs lived here (inherited verbatim from the legacy CRA app, so this
 * was never a cutover regression — reported 2026-08-21):
 *
 * 1. The WorkMarket-id branch was INVERTED. `WORKMARKET_CONTRACTOR_TYPES`
 *    ("V.C." / "1099") are precisely the types that HAVE a WorkMarket id — the
 *    contact card only renders the field for them — yet they were the ones the
 *    check SKIPPED. A 1099 with every other flag green and no WorkMarket id was
 *    reported "complete" and rendered in the default color, which is exactly
 *    what the client saw: a contractor who is not fully onboarded showing no
 *    warning color at all.
 *
 * 2. `workBookId` was compared with `=== true` alongside the boolean flags, but
 *    it is a STRING id ("100972"). That comparison can never pass, so whichever
 *    type the branch did include (w2) was permanently marked incomplete and
 *    always painted yellow — a false positive nobody questioned because yellow
 *    looked plausible.
 *
 * The id is now checked for presence, not for `true`. Empty string counts as
 * missing: prod stores `workBookId: ''` (not null) for contractors who never
 * got one, so `!= null` alone would let them through.
 */
const checkIfContractorProfileIsComplete = ({ data }) => {
  const requiresWorkmarketId = WORKMARKET_CONTRACTOR_TYPES?.includes(
    data?.contractorType,
  );

  const booleanFlagsComplete = [
    "isPersonalInformationAdded",
    "isRoleInformationAdded",
    "smsPermissionStatus",
    "smsConsentStatusBySms",
    "isAgreementAccepted",
  ]?.every((i) => data?.[i] === true);

  const workmarketIdComplete = requiresWorkmarketId ? !!data?.workBookId : true;

  return booleanFlagsComplete && workmarketIdComplete;
};

/**
 * contractor colors
 */
export const assignedContracotorTextColors = {
  contractorRestricted: "rgb(253, 53, 90)", // red shade 1
  contractorFlagged: "rgb(253, 53, 90)", // red shade 1
  noEmailAvailable: "#00e4ff", // cyan
  noMobileAvailable: "#627ce2", // violet
  pendingCompletion: "#face70", // yellow
};

/**
 * Restricted and flagged contractors use one system-wide alert color.
 *
 * This helper is intentionally limited to account warnings, so search/list
 * surfaces can use it without inheriting missing-email/onboarding colors.
 */
export const getContractorAlertTextColor = ({ data }) => {
  return data?.isContractorRestricted === true ||
    data?.isContractorFlagged === true
    ? assignedContracotorTextColors.contractorFlagged
    : null;
};

/**
 * contractor text colors reason array
 */
export const contractorTextColorReasonArray = [
  {
    color: assignedContracotorTextColors?.contractorRestricted,
    reason: "Contractor is Restricted",
  },
  {
    color: assignedContracotorTextColors?.contractorFlagged,
    reason: "Contractor is Flagged",
  },
  {
    color: assignedContracotorTextColors?.noEmailAvailable,
    reason: "Email is missing on Contractor Details",
  },
  {
    color: assignedContracotorTextColors?.noMobileAvailable,
    // Copy-paste leftover: this row is the violet "no phone" color, not email.
    reason: "Mobile is missing on Contractor Details",
  },
  {
    color: assignedContracotorTextColors?.pendingCompletion,
    reason: "Completion status of contractor is pending",
  },
];

/**
 * get contractor color based on if it's provisional or don't have email color
 */
export const getNewlyAssignedContractorColor = ({
  data,
  allowPendingProfile = false,
  isContractorAssigned = true,
}) => {
  if (isContractorAssigned == false) return null;
  const alertColor = getContractorAlertTextColor({ data });
  if (alertColor) return alertColor;
  if (getEmailSanitized({ email: data?.email }) == "")
    return assignedContracotorTextColors?.noEmailAvailable;
  if (data?.email && !data?.mobile && !data?.phone)
    return assignedContracotorTextColors?.noMobileAvailable;
  if (!checkIfContractorProfileIsComplete({ data }) && allowPendingProfile) {
    return assignedContracotorTextColors?.pendingCompletion;
  }
  return null;
};
